import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "../atoms/Avatar";
import { useAdminAuthStore } from "@/store/useAdminAuthStore";
import { useLogout } from "@/hooks/api/useAuthAPI";
import { capitalizeFirstLetter } from "@/utils/textUtils";
import { ArrowUp2, ArrowDown2, Logout, Setting2, Profile } from "iconsax-react";

export default function UserMenu() {
  const { adminUser, logout: logoutStore } = useAdminAuthStore();
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = adminUser?.role
    ? capitalizeFirstLetter(adminUser.role)
    : "Admin";

  const displayEmail = adminUser?.email;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      logoutStore();
      navigate("/login");
    }
  };

  const menuItems = [
    {
      icon: <Profile size={16} color="#FEFEFE" variant="Linear" />,
      label: "Profile",
      onClick: () => {
        navigate("/settings");
        setIsOpen(false);
      },
    },
    {
      icon: <Setting2 size={16} color="#FEFEFE" variant="Linear" />,
      label: "Settings",
      onClick: () => {
        navigate("/settings");
        setIsOpen(false);
      },
    },
    {
      icon: <Logout size={16} color="#FF6B6B" variant="Linear" />,
      label: "Logout",
      onClick: handleLogout,
      danger: true,
      loading: logoutMutation.isPending,
    },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between hover:bg-dark-20 p-2 rounded-[5px] transition-colors cursor-pointer"
      >
        <div className="flex flex-row items-center gap-2">
          <Avatar src="/profile.png" alt={displayName} />
          <div className="text-left">
            <p className="font-medium text-sm text-dark-text">{displayName}</p>
            <p className="font-normal text-xsm leading-xsm text-[#B7B7B7]">
              {displayEmail}
            </p>
          </div>
        </div>

        <div className="flex flex-col">
          <ArrowUp2
            variant="Linear"
            size={10.67}
            color="#FEFEFE"
            className="-mb-1"
          />
          <ArrowDown2 variant="Linear" size={10.67} color="#FEFEFE" />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-dark-20 border border-dark-30 rounded-[5px] shadow-lg overflow-hidden z-50">
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={item.onClick}
              disabled={item.loading}
              className={`cursor-pointer w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors disabled:opacity-50 ${
                item.danger
                  ? "text-error hover:bg-error/10"
                  : "text-dark-text hover:bg-dark-30"
              } ${
                index !== menuItems.length - 1 ? "border-b border-dark-30" : ""
              }`}
            >
              {item.loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
              ) : (
                item.icon
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
